import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, Target, CheckCircle2, XCircle, Calendar, AlertTriangle, MessageSquare } from "lucide-react";

const CARDS = [
  { key: "new", label: "My New Leads", icon: Target, color: "emerald" },
  { key: "hot", label: "Hot Leads", icon: Flame, color: "rose" },
  { key: "high", label: "High Score", icon: Target, color: "orange" },
  { key: "qualified", label: "Qualified", icon: CheckCircle2, color: "indigo" },
  { key: "won", label: "Won", icon: CheckCircle2, color: "emerald" },
  { key: "lost", label: "Lost", icon: XCircle, color: "slate" },
];

export default function SalesDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/sales/dashboard").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  const fuColor = (k) => ({ today: "emerald", overdue: "rose", upcoming: "indigo" }[k]);
  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-7xl mx-auto" data-testid="sales-dashboard">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-slate-500 mt-1">Your leads and follow-ups at a glance</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map((c) => (
          <Card key={c.key} className="p-4 border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => nav("/sales/leads")} data-testid={`sales-card-${c.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 rounded-lg bg-${c.color}-50 text-${c.color}-700 flex items-center justify-center`}>
                <c.icon className="w-4 h-4"/>
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{data.counts[c.key] || 0}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["today", "overdue", "upcoming"].map((k) => (
          <Card key={k} className={`p-5 border-slate-200 cursor-pointer hover:shadow-md`} onClick={() => nav("/sales/follow-ups")} data-testid={`fu-card-${k}`}>
            <div className="flex items-center gap-2 mb-3">
              {k === "overdue" ? <AlertTriangle className="w-5 h-5 text-rose-600"/> : <Calendar className={`w-5 h-5 text-${fuColor(k)}-600`}/>}
              <div className="font-semibold text-slate-900 capitalize">{k} follow-ups</div>
            </div>
            <div className={`text-4xl font-bold text-${fuColor(k)}-700`}>{data.follow_ups[k] || 0}</div>
            <div className="text-xs text-slate-500 mt-1">
              {k === "today" && "due today"}
              {k === "overdue" && "past due — act now"}
              {k === "upcoming" && "in the next few days"}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600"/>
            <h2 className="font-semibold text-slate-900">Recent conversations</h2>
          </div>
          <button onClick={()=>nav("/sales/inbox")} className="text-xs text-emerald-700 hover:underline" data-testid="open-inbox-link">Open inbox →</button>
        </div>
        {(data.recent_conversations || []).length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8">No recent conversations from your assigned leads.</div>
        ) : (
          <div className="space-y-2">
            {data.recent_conversations.map((c) => (
              <div key={c.id} className="flex items-start justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer"
                onClick={() => nav(`/sales/inbox/${c.id}`)} data-testid={`sales-conv-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{c.last_message_preview || "(no messages)"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ""}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">{c.state.replace("_"," ")}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
